import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  createFilterOptions,
} from '@mui/material';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  EventAwardRow,
  EventAwardInput,
  EventParticipantRow,
  EventSponsorshipRow,
  AWARD_IMAGE_MAX_BYTES,
} from '../hooks/useEventManagement';

/**
 * 賞品の授与 (表彰・抽選) の追加・編集ダイアログ。
 *
 * - 協賛品を選ぶと賞品名に商品名を入れる (当社で用意した賞品は協賛品なし)
 * - 当選者は空のままでもよい (当選者未定の賞品枠。当選者は後から一括取込か、この画面で入れる)
 * - 賞品の画像は 1 行に 1 枚。選んだ画像・外す操作は「保存する」で反映する (追加は行を作ってから付ける)
 * - 受け取りは一覧の「受け取り」列で切り替える
 */

// 賞品の画像の変更 (keep はそのまま、remove は外す、File は新しい画像を付ける)
export type AwardImageChange = 'keep' | 'remove' | File;

type FormState = {
  award_type: '表彰' | '抽選';
  award_name: string;
  sort_order: string;
  sponsorship_id: number | null;
  prize_name: string;
  participant_id: number | null;
  note: string;
};

const personLabel = (p: EventParticipantRow): string => `${p.last_name ?? ''} ${p.first_name ?? ''}`.trim() || '(氏名なし)';

// 当選者の候補は氏名・フリガナ・会社名・受付NO・配置で探せるようにする
const filterParticipants = createFilterOptions<EventParticipantRow>({
  stringify: (p) =>
    [personLabel(p), `${p.last_name ?? ''}${p.first_name ?? ''}`, `${p.last_kana ?? ''}${p.first_kana ?? ''}`, p.company_name, p.reception_no, p.table_no]
      .filter(Boolean)
      .join(' '),
});

const filterSponsorships = createFilterOptions<EventSponsorshipRow>({
  stringify: (s) => [s.reception_no, s.company_name, s.product_name].filter(Boolean).join(' '),
});

const EventAwardDialog = React.memo(function EventAwardDialog({
  open,
  mode,
  award,
  participants,
  sponsorships,
  assignedBySponsorship,
  groupLabel,
  onClose,
  onSave,
}: {
  open: boolean;
  mode: 'add' | 'edit';
  // 編集対象 (mode=edit のみ)
  award?: EventAwardRow | null;
  participants: EventParticipantRow[];
  sponsorships: EventSponsorshipRow[];
  // 協賛品ごとの割当済みの行数 (賞品の授与の sponsorship_id を数えたもの)
  assignedBySponsorship: Map<number, number>;
  // 配置のグループの呼び名 (卓・組)
  groupLabel: string;
  onClose: () => void;
  // input は編集で項目を変えていなければ null (画像だけの変更)
  onSave: (input: EventAwardInput | null, image: AwardImageChange) => Promise<boolean>;
}) {
  const [form, setForm] = useState<FormState>({
    award_type: '抽選',
    award_name: '',
    sort_order: '',
    sponsorship_id: null,
    prize_name: '',
    participant_id: null,
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const [image, setImage] = useState<AwardImageChange>('keep');
  const [imageError, setImageError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 選んだ画像のプレビュー URL (閉じる・選び直すときに解放する)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!(image instanceof File)) {
      setPendingUrl(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPendingUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  const savedImageUrl = mode === 'edit' ? award?.image_url ?? null : null;
  const previewUrl = pendingUrl ?? (image === 'keep' ? savedImageUrl : null);

  const pickImage = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageError('画像ファイル (JPEG / PNG / GIF / WebP) を選んでください');
      return;
    }
    if (file.size > AWARD_IMAGE_MAX_BYTES) {
      setImageError('画像が大きすぎます (20MB まで)');
      return;
    }
    setImageError('');
    setImage(file);
  };

  // 開いたときに対象の行 (追加は空欄) で初期化する
  useEffect(() => {
    if (!open) return;
    setImage('keep');
    setImageError('');
    setForm(
      mode === 'edit' && award
        ? {
            award_type: award.award_type,
            award_name: award.award_name,
            sort_order: award.sort_order != null ? String(award.sort_order) : '',
            sponsorship_id: award.sponsorship_id,
            prize_name: award.prize_name ?? '',
            participant_id: award.participant_id,
            note: award.note ?? '',
          }
        : { award_type: '抽選', award_name: '', sort_order: '', sponsorship_id: null, prize_name: '', participant_id: null, note: '' },
    );
  }, [open, mode, award]);

  const sponsorshipById = useMemo(() => new Map(sponsorships.map((s) => [s.id, s])), [sponsorships]);
  const participantById = useMemo(() => new Map(participants.map((p) => [p.id, p])), [participants]);
  const selectedSponsorship = form.sponsorship_id != null ? sponsorshipById.get(form.sponsorship_id) ?? null : null;
  const selectedParticipant = form.participant_id != null ? participantById.get(form.participant_id) ?? null : null;

  // 割当済みの数。編集中の行が元から割り当てている協賛品は、その行を数えない
  const assignedOthers = (s: EventSponsorshipRow): number =>
    (assignedBySponsorship.get(s.id) ?? 0) - (mode === 'edit' && award?.sponsorship_id === s.id ? 1 : 0);

  // 協賛品を選んだら賞品名に商品名を入れる (手で直した賞品名は前の協賛品の商品名と違うので残す)
  const handleSponsorship = (s: EventSponsorshipRow | null) => {
    setForm((prev) => {
      const prevProduct = prev.sponsorship_id != null ? sponsorshipById.get(prev.sponsorship_id)?.product_name ?? '' : '';
      const keepName = prev.prize_name.trim() !== '' && prev.prize_name !== prevProduct;
      return { ...prev, sponsorship_id: s?.id ?? null, prize_name: keepName ? prev.prize_name : s?.product_name ?? '' };
    });
  };

  const orderValid = form.sort_order.trim() === '' || /^\d+$/.test(form.sort_order.trim());
  const canSave = !!form.award_name.trim() && orderValid && !saving;
  const winnerChanged = mode === 'edit' && !!award?.is_received && form.participant_id !== award.participant_id;

  const handleSave = async () => {
    if (!canSave) return;
    const input: EventAwardInput = {
      award_type: form.award_type,
      award_name: form.award_name.trim(),
      sort_order: form.sort_order.trim() === '' ? null : parseInt(form.sort_order.trim(), 10),
      sponsorship_id: form.sponsorship_id,
      prize_name: form.prize_name.trim() || null,
      participant_id: form.participant_id,
      note: form.note.trim() || null,
    };
    // 編集で項目を変えていなければ行は保存しない (画像だけの変更で再取得しない)
    const unchanged =
      mode === 'edit' &&
      !!award &&
      input.award_type === award.award_type &&
      input.award_name === award.award_name &&
      (input.sort_order ?? null) === (award.sort_order ?? null) &&
      input.sponsorship_id === award.sponsorship_id &&
      input.prize_name === (award.prize_name ?? null) &&
      input.participant_id === award.participant_id &&
      input.note === (award.note ?? null);
    if (unchanged && image === 'keep') {
      onClose();
      return;
    }
    setSaving(true);
    const ok = await onSave(unchanged ? null : input, image);
    setSaving(false);
    if (ok) onClose();
  };

  const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'edit' ? <Icons.Edit sx={{ fontSize: 18 }} /> : <Icons.CardGiftcard sx={{ fontSize: 18 }} />}
        {mode === 'edit' ? '賞品の授与の編集' : '賞品の追加'}
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) 90px', gap: 1.5, alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={form.award_type}
            onChange={(_, v) => v && setForm((prev) => ({ ...prev, award_type: v }))}
            sx={{ '& .MuiToggleButton-root': { fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.5 } }}
          >
            <ToggleButton value="抽選">抽選</ToggleButton>
            <ToggleButton value="表彰">表彰</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="賞の名前"
            required
            placeholder="特賞・1等・優勝 など"
            value={form.award_name}
            onChange={(e) => setForm((prev) => ({ ...prev, award_name: e.target.value }))}
            sx={fieldSx}
            autoFocus={mode === 'add'}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            size="small"
            label="発表順"
            placeholder="末尾"
            value={form.sort_order}
            onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
            error={!orderValid}
            sx={fieldSx}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Box>

        <Autocomplete
          options={sponsorships}
          value={selectedSponsorship}
          onChange={(_, v) => handleSponsorship(v)}
          filterOptions={filterSponsorships}
          getOptionLabel={(s) => `${s.company_name}　${s.product_name ?? '(商品名未入力)'}`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="該当する協賛品はありません"
          renderOption={(props, s) => {
            const quantity = s.quantity ?? 1;
            const used = assignedOthers(s);
            return (
              <li {...props} key={s.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', flexShrink: 0 }}>{s.reception_no}</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.product_name ?? '(商品名未入力)'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.company_name}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, flexShrink: 0, color: used >= quantity ? '#94a3b8' : '#d97706' }}>
                    割当 {used}/{s.quantity ?? '-'}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} size="small" label="協賛品" placeholder="空欄は当社で用意した賞品" sx={fieldSx} />
          )}
        />

        <TextField
          size="small"
          label="賞品名"
          value={form.prize_name}
          onChange={(e) => setForm((prev) => ({ ...prev, prize_name: e.target.value }))}
          sx={fieldSx}
          inputProps={{ maxLength: 150 }}
        />

        {/* 賞品の画像。枠をクリックするか、画像をドラッグして置く */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box
            onClick={() => !saving && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!saving) pickImage(e.dataTransfer.files?.[0]);
            }}
            sx={{
              width: 128,
              height: 96,
              flexShrink: 0,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: dragOver ? '#3b82f6' : '#cbd5e1',
              bgcolor: dragOver ? '#eff6ff' : '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            {previewUrl ? (
              <Box component="img" src={previewUrl} alt="賞品の画像" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
                <Icons.Image sx={{ fontSize: 28 }} />
                <Typography sx={{ fontSize: '11px' }}>画像を置く</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', mb: 0.5 }}>賞品の画像</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                startIcon={<Icons.Image sx={{ fontSize: 16 }} />}
                sx={STANDARD_TEXT_BUTTON_SX}
              >
                {previewUrl ? '差し替える' : '画像を選ぶ'}
              </Button>
              {previewUrl && (
                // 選んだばかりの画像は選択の取り消し (保存済みの画像に戻る)、保存済みの画像は外す
                <Button
                  size="small"
                  onClick={() => setImage(image instanceof File ? 'keep' : 'remove')}
                  disabled={saving}
                  startIcon={<Icons.Delete sx={{ fontSize: 16 }} />}
                  sx={STANDARD_TEXT_BUTTON_SX}
                >
                  {image instanceof File && savedImageUrl ? '選び直しをやめる' : '外す'}
                </Button>
              )}
              {image === 'remove' && (
                <Button size="small" onClick={() => setImage('keep')} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
                  外すのをやめる
                </Button>
              )}
            </Box>
            <Typography sx={{ fontSize: '11px', color: imageError ? '#dc2626' : '#94a3b8', mt: 0.25 }}>
              {imageError ||
                (image === 'remove'
                  ? '保存すると画像を外します'
                  : image instanceof File
                    ? `保存すると「${image.name}」を付けます`
                    : 'JPEG・PNG・GIF・WebP (20MB まで)。幅 1200px までに縮小して保存します')}
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            onChange={(e) => {
              pickImage(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </Box>

        <Autocomplete
          options={participants}
          value={selectedParticipant}
          onChange={(_, v) => setForm((prev) => ({ ...prev, participant_id: v?.id ?? null }))}
          filterOptions={filterParticipants}
          getOptionLabel={(p) => `${personLabel(p)}（${p.company_name}）`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="該当する参加者はいません"
          renderOption={(props, p) => (
            <li {...props} key={p.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', flexShrink: 0 }}>{p.reception_no}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>{personLabel(p)}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.company_name}
                </Typography>
                {p.table_no && (
                  <Typography sx={{ fontSize: '11px', color: '#64748b', flexShrink: 0 }}>
                    {groupLabel} {p.table_no}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} size="small" label="当選者・受賞者" placeholder="空欄は当選者未定" sx={fieldSx} />
          )}
        />
        {winnerChanged && (
          <Typography sx={{ fontSize: '12px', color: '#b45309', display: 'flex', alignItems: 'center', gap: 0.5, mt: -0.5 }}>
            <Icons.WarningAmber sx={{ fontSize: 16 }} />
            当選者を変えると、受け取りは「未」に戻ります
          </Typography>
        )}

        <TextField
          size="small"
          label="備考"
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
          sx={fieldSx}
          inputProps={{ maxLength: 200 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} /> : mode === 'edit' ? <Icons.Save /> : <Icons.Add />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          {mode === 'edit' ? '保存する' : '追加する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EventAwardDialog;
